import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Session Orchestrator Service
 * @class SessionOrchestratorService
 * @description Service for orchestrating session abort.
 * Contains methods for aborting the global session and scopes and channels.
 * @since 1.0.0
 * @author SADIN Development Team
 */
@Injectable()
export class SessionOrchestratorService {

  /**
   * Global Abort Controller
   * @private
   * @readonly
   * @type {Subject<void>}
   * @description Global Abort Subject
   * @example
   * ```typescript
   * const global$ = new Subject<void>();
   * global$.next();
   * ```
   */
  private readonly _global$: Subject<void> = new Subject<void>();

  /**
   * Scopes Map
   * @private
   * @readonly
   * @type {Map<string, Subject<void>>}
   * @description Scopes Map
   * @example
   * ```typescript
   * const scopes = new Map<string, Subject<void>>();
   * scopes.set('scope1', new Subject<void>());
   * scopes.set('scope2', new Subject<void>());
   * ```
   */
  private readonly _scopes: Map<string, Subject<void>> = new Map<string, Subject<void>>();

  /**
   * Channels Map
   * @private
   * @readonly
   * @type {Map<string, Subject<void>>}
   * @description Channels Map
   * @example
   * ```typescript
   * const channels = new Map<string, Subject<void>>();
   * channels.set('channel1', new Subject<void>());
   * channels.set('channel2', new Subject<void>());
   * ```
   */
  private readonly _channels = new Map<string, Subject<void>>();

  /**
   * Get Global Abort Subject
   * @readonly
   * @type {Subject<void>}
   * @description Global Abort Subject
   */
  public get global$(): Observable<void> {
    return this._global$.asObservable();
  }

  /**
   * Constructor
   * @constructor
   * @description Constructor
   */
  constructor() { }

  /**
   * Abort Global
   * @description Abort Global
   */
  public abortGlobal(): void {
    this._global$.next();
  }

  /**
   * Get Scope
   * @param {string} scope - Scope name
   * @returns {Subject<void> | null} Scope subject or null if scope is not valid
   * @description Get Scope
   */
  public scope(scope: string): Observable<void> | null {

    // if scope is not valid, return null
    if (!scope) return null;

    // get scope
    let s = this._scopes.get(scope);

    // if scope does not exist, create it
    if (!s) {
      // create scope subject
      s = new Subject<void>();
      // set scope
      this._scopes.set(scope, s);
    }

    return s.asObservable();

  }
  
  /**
   * 
   * @param scope - Scope name
   * @returns {void}
   * @description Aborts the scope.
   * @example
   * ```typescript
   * this.sessionOrchestratorService.abortScope('scope1');
   * ```
   */
  public abortScope(scope: string): void {

    // get scope
    const _scope = this._scopes.get(scope);

    if (!_scope) return;

    // abort scope
    _scope.next();

  }

  /**
   *
   * @param name - Channel name
   * @returns {Observable<void> | null} Channel subject or null if channel is not valid
   * @description Get Channel
   * @example
   * ```typescript
   * this.sessionOrchestratorService.channel$('channel1');
   * ```
   */
  public channel$(name: string): Observable<void> | null{
    
    // if scope is not valid, return null
    if (!name) return null;

    // get scope
    let s = new Subject<void>();
    let _channel = this._channels.get(name);

    if (_channel) {
      this._channels.set(name, s);
    }
    
    return s.asObservable();
  }

  /**
   * 
   * @param name - Channel name
   * @returns {void}
   * @description Aborts the channel.
   * @example
   * ```typescript
   * this.sessionOrchestratorService.abortChannel('channel1');
   * ```
   */
  public abortChannel(name: string): void {
    
    // get channel
    const _channel = this._channels.get(name);

    if (!_channel) return;

    // abort _channel
    _channel.next();

  }

}